import { useRef, useState, type TouchEvent } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useLocale } from '../i18n/LocaleContext'
import PlaceholderImage from './ui/PlaceholderImage'

export interface Person {
  name: string
  role: string
  bio: string
  photo: string
}

function PersonCard({ person }: { person: Person }) {
  return (
    <div className="bg-white rounded-card overflow-hidden shadow-sm hover:shadow-md transition-shadow h-full">
      {person.photo ? (
        <img
          src={person.photo}
          alt={person.name}
          width={400}
          height={256}
          className="w-full h-64 object-cover"
          loading="lazy"
        />
      ) : (
        <PlaceholderImage label={person.name} variant="camp" className="w-full h-64" />
      )}
      <div className="p-6 text-center">
        <h3 className="font-heading font-semibold text-lg text-secondary mb-1">{person.name}</h3>
        <p className="text-primary font-medium text-sm mb-3">{person.role}</p>
        <p className="text-text-secondary text-sm leading-relaxed">{person.bio}</p>
      </div>
    </div>
  )
}

/**
 * Renders a set of people (team members) two ways:
 *  - Desktop (md+): a centered flex-wrap grid, 3 cards per row. Because it wraps
 *    with `justify-center`, an incomplete final row is centered automatically —
 *    e.g. 5 people show as 3 + a centered row of 2.
 *  - Mobile (<md): a one-card swipeable carousel with touch gestures, prev/next
 *    arrows, and dot indicators.
 * Used by both the homepage Heroes section and the About team section.
 */
export default function PeopleCarousel({ people }: { people: Person[] }) {
  const { locale } = useLocale()
  const [index, setIndex] = useState(0)
  const touchStartX = useRef<number | null>(null)
  const touchDeltaX = useRef(0)

  if (people.length === 0) return null

  const labels =
    locale === 'hu'
      ? { region: 'Csapattagok', prev: 'Előző', next: 'Következő', slide: 'Ugrás ide' }
      : { region: 'Membrii echipei', prev: 'Anterior', next: 'Următor', slide: 'Mergi la' }

  const last = people.length - 1
  const go = (i: number) => setIndex(Math.max(0, Math.min(last, i)))

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchDeltaX.current = 0
  }
  const onTouchMove = (e: TouchEvent) => {
    if (touchStartX.current !== null) {
      touchDeltaX.current = e.touches[0].clientX - touchStartX.current
    }
  }
  const onTouchEnd = () => {
    const threshold = 50
    if (touchDeltaX.current > threshold) go(index - 1)
    else if (touchDeltaX.current < -threshold) go(index + 1)
    touchStartX.current = null
    touchDeltaX.current = 0
  }

  return (
    <>
      {/* Desktop: centered wrap grid — 3 per row, incomplete last row centered */}
      <div className="hidden md:flex md:flex-wrap md:justify-center gap-8">
        {people.map((person, i) => (
          <div key={i} className="md:w-[calc((100%_-_4rem)/3)]">
            <PersonCard person={person} />
          </div>
        ))}
      </div>

      {/* Mobile: swipeable carousel */}
      <div className="md:hidden">
        <div
          className="overflow-hidden"
          role="region"
          aria-roledescription="carousel"
          aria-label={labels.region}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="flex transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {people.map((person, i) => (
              <div
                key={i}
                className="w-full flex-shrink-0 px-1"
                aria-hidden={i !== index}
              >
                <PersonCard person={person} />
              </div>
            ))}
          </div>
        </div>

        {people.length > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              type="button"
              onClick={() => go(index - 1)}
              disabled={index === 0}
              aria-label={labels.prev}
              className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-secondary disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChevronLeft size={20} />
            </button>

            <div className="flex gap-2">
              {people.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => go(i)}
                  aria-label={`${labels.slide} ${i + 1}`}
                  aria-current={i === index}
                  className={`h-2 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                    i === index ? 'w-6 bg-primary' : 'w-2 bg-border'
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => go(index + 1)}
              disabled={index === last}
              aria-label={labels.next}
              className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-secondary disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </>
  )
}
